import { useState, useEffect } from 'react';
import { Modal, Button } from 'animal-island-ui';
import { useAppStore } from '../store/useAppStore';

const API_BASE = 'https://api.xishia.com/app';

interface AuthDialogProps {
  open: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'register';
  onSuccess?: () => void;
}

export default function AuthDialog({ open, onClose, initialMode = 'login', onSuccess }: AuthDialogProps) {
  const [mode, setMode] = useState<'login' | 'register'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [nickname, setNickname] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [error, setError] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  const { setAuthToken, setUser } = useAppStore();

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setEmail('');
      setPassword('');
      setCode('');
      setNickname('');
      setError('');
      setCodeSent(false);
    }
  }, [open, initialMode]);

  const handleSendCode = async () => {
    if (!email.trim()) {
      setError('请输入邮箱');
      return;
    }
    setSendingCode(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/auth/send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, type: 'register' }),
      });
      const data = await res.json();
      if (data.code === 0) {
        setCodeSent(true);
      } else {
        setError(data.message || '发送失败');
      }
    } catch (e) {
      setError('网络错误');
    } finally {
      setSendingCode(false);
    }
  };

  const handleRegister = async () => {
    if (!email.trim() || !password.trim() || !code.trim()) {
      setError('请填写完整信息');
      return;
    }
    if (password.length < 6) {
      setError('密码至少6位');
      return;
    }
    setError('');
    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, code, nickname: nickname.trim() || undefined }),
      });
      const data = await res.json();
      if (data.code === 0) {
        setAuthToken(data.data.token);
        setUser(data.data.user);
        onSuccess?.();
        onClose();
      } else {
        setError(data.message || '注册失败');
      }
    } catch (e) {
      setError('网络错误');
    }
  };

  const handleLogin = async () => {
    if (!email.trim() || !password.trim()) {
      setError('请填写完整信息');
      return;
    }
    setError('');
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (data.code === 0) {
        setAuthToken(data.data.token);
        setUser(data.data.user);
        onSuccess?.();
        onClose();
      } else {
        setError(data.message || '登录失败');
      }
    } catch (e) {
      setError('网络错误');
    }
  };

  return (
    <Modal
      open={open}
      title={mode === 'login' ? '登录' : '注册'}
      onClose={onClose}
      onOk={mode === 'login' ? handleLogin : handleRegister}
      typewriter={false}
    >
      <div className="py-4 space-y-4">
        {error && (
          <div className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm text-gray-600 mb-1">邮箱</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border-2 border-amber-200 focus:border-amber-400 focus:outline-none bg-amber-50"
            placeholder="your@email.com"
            autoFocus={mode === 'login'}
          />
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">密码</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border-2 border-amber-200 focus:border-amber-400 focus:outline-none bg-amber-50"
            placeholder="至少6位"
          />
        </div>

        {mode === 'register' && (
          <>
            <div>
              <label className="block text-sm text-gray-600 mb-1">昵称（可选）</label>
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-amber-200 focus:border-amber-400 focus:outline-none bg-amber-50"
                placeholder="默认取邮箱前缀"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">验证码</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="flex-1 px-4 py-2.5 rounded-xl border-2 border-amber-200 focus:border-amber-400 focus:outline-none bg-amber-50"
                  placeholder="6位验证码"
                  maxLength={6}
                />
                <Button
                  type="primary"
                  onClick={handleSendCode}
                  disabled={sendingCode || codeSent}
                  className="whitespace-nowrap"
                >
                  {codeSent ? '已发送' : sendingCode ? '发送中...' : '发送验证码'}
                </Button>
              </div>
              {codeSent && (
                <p className="text-xs text-gray-500 mt-1">验证码已发送到邮箱，5分钟内有效</p>
              )}
            </div>
          </>
        )}

        <div className="flex items-center justify-center gap-2 pt-2">
          <span className="text-sm text-gray-500">
            {mode === 'login' ? '没有账号？' : '已有账号？'}
          </span>
          <button
            onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            className="text-sm font-medium text-red-500 hover:underline"
          >
            {mode === 'login' ? '注册' : '登录'}
          </button>
        </div>
      </div>
    </Modal>
  );
}